import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import LoadingScreen from '../ui/LoadingScreen';
import { useEffect, useState } from 'react';

const ProtectedRoute = () => {
  const { user, isLoading } = useAuth();
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [authTimeout, setAuthTimeout] = useState(false);
  
  // Add a slight delay before showing loading screen to prevent flashes
  useEffect(() => {
    let timer: number | undefined;
    
    if (isLoading) {
      timer = window.setTimeout(() => {
        setShowLoadingScreen(true);
      }, 300); // Small delay to prevent flashing
    } else {
      setShowLoadingScreen(false);
      setAuthTimeout(false); // Reset timeout flag when loading completes
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isLoading]);

  // Force resolve loading after timeout to prevent infinite loading state
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (showLoadingScreen) {
        console.warn('Loading timeout reached in ProtectedRoute, forcing navigation decision');
        setAuthTimeout(true);
        setShowLoadingScreen(false);
      }
    }, 8000); // Increased timeout for better reliability, especially in production

    return () => clearTimeout(timeout);
  }, [showLoadingScreen]);

  // If we still have a user session in localStorage but auth check is timing out,
  // assume the user is authenticated to prevent blocking navigation
  useEffect(() => {
    if (authTimeout) {
      try {
        // Check if we have a session in localStorage
        const hasLocalSession = localStorage.getItem('sb-supabase-auth-token');
        if (hasLocalSession) {
          console.log('Auth timeout but local session found - assuming authenticated');
          
          // For deployed sites, get deployment-specific diagnostic info
          if (import.meta.env.PROD) {
            try {
              const diagnosticInfo = {
                lastAuthEvent: localStorage.getItem('lastAuthEvent'),
                lastAuthEventTime: localStorage.getItem('lastAuthEventTime'),
                lastAuthCheck: localStorage.getItem('lastAuthCheck'),
                lastRoleCheck: localStorage.getItem('lastRoleCheck'),
                authInitStart: localStorage.getItem('authInitStart'),
                authInitComplete: localStorage.getItem('authInitComplete'),
                browserUserAgent: navigator.userAgent,
                url: window.location.href,
                timestamp: new Date().toISOString()
              };
              
              console.log('Auth diagnostic info:', diagnosticInfo);
              sessionStorage.setItem('authDiagnostics', JSON.stringify(diagnosticInfo));
            } catch (e) {
              // Ignore errors
            }
          }
        }
      } catch (e) {
        console.error('Error checking local session:', e);
      }
    }
  }, [authTimeout]);

  // If we're showing loading screen, render it
  if (showLoadingScreen) {
    return <LoadingScreen />;
  }

  // If not loading and no user, redirect to login
  if (!isLoading && !user && !authTimeout) {
    // Store current path for potential redirect back after login
    try {
      sessionStorage.setItem('redirectPath', window.location.pathname);
    } catch (e) {
      // Ignore storage errors
    }
    return <Navigate to="/login" replace />;
  }
  
  // For production environments: if we have a timeout but also have local session data,
  // allow access to prevent blocking the user unnecessarily
  if (authTimeout) {
    try {
      const hasLocalSession = localStorage.getItem('sb-supabase-auth-token');
      if (hasLocalSession) {
        console.log('Allowing access despite timeout due to local session');
        return <Outlet />;
      }
    } catch (e) {
      // Ignore storage errors
    }
    
    // If no valid session, redirect to login
    return <Navigate to="/login" replace />;
  }
  
  // Otherwise, render the children routes
  return <Outlet />;
};

export default ProtectedRoute;